import React, { useState, useEffect, useMemo } from "react";
import Chart, {
  ArgumentAxis,
  Series,
  ValueAxis,
  Tooltip,
  Legend
} from "devextreme-react/cjs/chart";
import { Card, Form, Row, Col } from "react-bootstrap";

const palette = ["#3058A6", "#169e07"];
const formatter = new Intl.NumberFormat("es-CO", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const LineChartCardWithRange = ({ title, dataRecords = [], styles, img }) => {
  // 1) Preparo y ordeno todos los datos (Hook)
  const fullDs = useMemo(() => {
    return dataRecords
      .map(item => {
        const [year, mon] = (item?.MesAnio || "").split("/");
        return {
          month:      item.MesAnio,
          monthInput: `${year}-${mon}`,
          planeado:   item.CostoPlaneado,
          real:       item.CostoReal
        };
      })
      .sort((a, b) => a.monthInput.localeCompare(b.monthInput));
  }, [dataRecords]);

  // 2) Establezco límites absolutos (pueden usarse inmediatamente)
  const minDate = fullDs.length ? fullDs[0].monthInput : "";
  const maxDate = fullDs.length
    ? fullDs[fullDs.length - 1].monthInput
    : "";

  // 3) Estado del rango de fechas (Hook)
  const [range, setRange] = useState({ start: minDate, end: maxDate });

  // 4) Aseguro que el rango siempre esté inicializado (Hook)
  useEffect(() => {
    if (fullDs.length && (!range.start || !range.end)) {
      setRange({ start: minDate, end: maxDate });
    }
  }, [fullDs, minDate, maxDate, range]);

  // 5) Dataset filtrado según el rango (Hook)
  const filteredDs = useMemo(() => {
    if (!range.start || !range.end) return fullDs;
    return fullDs.filter(
      item => item.monthInput >= range.start && item.monthInput <= range.end
    );
  }, [fullDs, range]);

  // 6) Últimos valores para la leyenda custom
  const last = filteredDs[filteredDs.length - 1] || { planeado: 0, real: 0 };

  // 7) Handlers de cambio de rango
  const onChangeStart = e => {
    const start = e.target.value;
    if (start < minDate || start > maxDate) return;
    setRange(r => ({
      start,
      end: start > r.end ? start : r.end
    }));
  };
  const onChangeEnd = e => {
    const end = e.target.value;
    if (end < minDate || end > maxDate) return;
    setRange(r => ({
      start: end < r.start ? end : r.start,
      end
    }));
  };

  // 8) Fallback si no hay datos
  if (!fullDs.length) {
    return (
      <Card className={styles.boxShadow}>
        <Card.Header className={styles.CardHeader}>
          {img} {title}
        </Card.Header>
        <Card.Body>
          <p>No hay datos disponibles.</p>
        </Card.Body>
      </Card>
    );
  }

  // 9) Render definitivo
  return (
    <Card className={styles.boxShadow}>
      <Card.Header className={styles.CardHeader}>
        {img} {title}
      </Card.Header>
      <Card.Body>
        {/* Selector de rango de meses */}
        <Form className="mb-3">
          <Row>
            <Col xs="auto">
              <Form.Label>Desde</Form.Label>
              <Form.Control
                type="month"
                value={range.start}
                onChange={onChangeStart}
                min={minDate}
                max={maxDate}
                style={{ maxWidth: "200px" }}
              />
            </Col>
            <Col xs="auto">
              <Form.Label>Hasta</Form.Label>
              <Form.Control
                type="month"
                value={range.end}
                onChange={onChangeEnd}
                min={minDate}
                max={maxDate}
                style={{ maxWidth: "200px" }}
              />
            </Col>
          </Row>
        </Form>

        {/* Gráfico */}
        <Chart dataSource={filteredDs} height={250}>
          <ArgumentAxis
            label={{
              overlappingBehavior: "rotate",
              rotationAngle:      -45,
              font: { size: 12 }
            }}
          />
          <ValueAxis />
          <Series
            argumentField="month"
            valueField="planeado"
            name="Planeado"
            type="line"
            color={palette[0]}
          />
          <Series
            argumentField="month"
            valueField="real"
            name="Real"
            type="line"
            color={palette[1]}
          />
          <Legend visible={false} />
          <Tooltip
            enabled
            shared={false}
            customizeTooltip={({ valueText, seriesName }) => ({
              text: `${seriesName}: $${formatter.format(
                Number(valueText)
              )}`
            })}
          />
        </Chart>

        {/* Leyenda custom */}
        <div className={styles.customLegend}>
          <div className={styles.customLegendItem}>
            <span
              className={styles.legendMarker}
              style={{ backgroundColor: palette[0] }}
            />
            <small className="ms-1">Planeado</small>
          </div>
          <div className={styles.customLegendItem}>
            <span
              className={styles.legendMarker}
              style={{ backgroundColor: palette[1] }}
            />
            <small className="ms-1">Real</small>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default LineChartCardWithRange;
